import { useState, type FormEvent } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button, Icon } from '../components/ui';
import { ApiError, resetPassword } from '../lib/api';
import './Login.css';
import './shared.css';

/**
 * The far end of a reset link.
 *
 * Setting a new password ends every session on the account, so this finishes
 * at the sign-in screen rather than dropping the person straight in — and they
 * will still be asked for their second factor, which is the point: a leaked
 * mailbox is not enough to reach the portal.
 */
export function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get('token') ?? '';
  const navigate = useNavigate();

  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await resetPassword(token, password);
      setDone(true);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'That did not work.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="login">
      <section className="login__panel">
        <div className="login__inner">
          <div className="login__brand">
            <span className="login__mark">{Icon.logo({ size: 18 })}</span>
            <span className="login__brandname">Fairmount</span>
          </div>

          {done ? (
            <div className="login__form">
              <h1 className="login__title">Password changed</h1>
              <p className="login__lede">
                Every device has been signed out. Sign in with the new password —
                your second factor is unchanged.
              </p>
              <Button variant="primary" size="lg" block onClick={() => navigate('/login')}>
                Go to sign in
              </Button>
            </div>
          ) : (
            <form className="login__form" onSubmit={submit}>
              <h1 className="login__title">Choose a new password</h1>
              <p className="login__lede">
                This link works once. Setting a password here signs out every
                device currently using this account.
              </p>

              {error && (
                <div className="notice notice--danger" role="alert">
                  {Icon.alert({ size: 15 })}
                  <span>{error}</span>
                </div>
              )}

              <div className="field">
                <label className="field__label" htmlFor="new-password">
                  New password
                </label>
                <input
                  id="new-password"
                  className="input input--lg"
                  type="password"
                  autoComplete="new-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                />
                <div className="field__hint">
                  A few unrelated words. Checked against known breaches.
                </div>
              </div>

              <Button
                type="submit"
                variant="primary"
                size="lg"
                block
                disabled={busy || !password || !token}
              >
                {busy ? 'Saving…' : 'Set password'}
              </Button>
            </form>
          )}
        </div>
      </section>

      <aside className="login__aside">
        <div className="login__asideinner">
          <h2 className="login__headline">
            A password is
            <br />
            only half of it.
          </h2>
          <ul className="login__points">
            <li>{Icon.check({ size: 15 })} Resets never bypass your second factor</li>
            <li>{Icon.check({ size: 15 })} Links are single-use and short-lived</li>
            <li>{Icon.check({ size: 15 })} Every device is signed out</li>
          </ul>
        </div>
      </aside>
    </div>
  );
}
